// Show-from / hide-after times. The server stores UTC for a fixed zone and a
// floating wall-clock time for "company time" (docs/news-api.md, "Times").
// The composer always edits the wall-clock value of an <input
// type="datetime-local"> ("YYYY-MM-DDTHH:mm") IN THE CHOSEN ZONE — never the
// browser's zone, which is what made scheduled announcements drift before.

function parts(date: Date, tz: string) {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const p: Record<string, string> = {}
  for (const { type, value } of f.formatToParts(date)) p[type] = value
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second }
}

function offsetMs(tz: string, date: Date): number {
  const p = parts(date, tz)
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(date.getTime() / 1000) * 1000
}

const WALL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/

/** "2026-09-29T07:00" in `tz` -> ISO UTC instant. */
export function wallToUtcIso(wall: string, tz: string): string | null {
  const m = WALL.exec(wall || '')
  if (!m) return null
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  const first = guess - offsetMs(tz, new Date(guess))
  const second = guess - offsetMs(tz, new Date(first))
  return new Date(second).toISOString()
}

/** ISO instant -> "YYYY-MM-DDTHH:mm" as read on a clock in `tz`. */
export function utcIsoToWall(iso: string | null, tz: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = parts(d, tz)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`
}

export function nowWall(tz: string): string {
  return utcIsoToWall(new Date().toISOString(), tz === 'company' ? 'America/New_York' : tz)
}

/** Human "Sep 29, 7:00 AM" for a wall-clock string. */
export function formatWall(wall: string): string {
  const m = WALL.exec(wall || '')
  if (!m) return ''
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]))
  return d.toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function browserZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}
